/**
 * DateRangeFilterDropdown
 *
 * A reusable date-range filter popover that matches the existing HarborDataTable
 * column-filter style (same panel chrome, same filter-alt icon colouring).
 *
 * Props
 * ─────
 * visible       – show/hide the modal
 * fromDate      – currently selected "from" date (or null)
 * toDate        – currently selected "to" date (or null)
 * top / left    – absolute position anchored below the triggering icon
 * onFromChange  – called when the user picks a new "from" date
 * onToChange    – called when the user picks a new "to" date
 * onClear       – called when the user taps "Clear"
 * onClose       – called when the backdrop is tapped (close without clearing)
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  Platform,
} from 'react-native';
import FontFamily from '../../../constants/fonts';
import LhDatePicker from '../../../components/LhDatePicker';
import { useDarkStyles } from '../../../theme/useDarkStyles';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DateRangeFilterDropdownProps {
  visible: boolean;
  fromDate: Date | null;
  toDate: Date | null;
  top: number;
  left: number;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
  onClear: () => void;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const DateRangeFilterDropdown: React.FC<DateRangeFilterDropdownProps> = ({
  visible,
  fromDate,
  toDate,
  top,
  left,
  onFromChange,
  onToChange,
  onClear,
  onClose,
}) => {
  const styles = useDarkStyles(baseStyles);
  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      {/* Backdrop – tapping outside closes without clearing */}
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.backdrop}>
          {/* Panel – tapping inside does NOT bubble to backdrop */}
          <TouchableWithoutFeedback onPress={() => { /* stop propagation */ }}>
            <View style={[styles.panel, { position: 'absolute', top, left }]}>

              {/* From Date */}
              <Text style={styles.label}>From Date</Text>
              <LhDatePicker
                value={fromDate}
                onChange={onFromChange}
                placeholder="Select date"
                maximumDate={toDate ?? undefined}
                style={{ containerStyle: styles.pickerContainer }}
              />

              {/* To Date */}
              <Text style={[styles.label, styles.labelTop]}>To Date</Text>
              <LhDatePicker
                value={toDate}
                onChange={onToChange}
                placeholder="Select date"
                minimumDate={fromDate ?? undefined}
                style={{ containerStyle: styles.pickerContainer }}
              />

              {/* Clear */}
              <View style={styles.clearRow}>
                <TouchableOpacity
                  onPress={() => {
                    onClear();
                    onClose();
                  }}
                >
                  <Text style={styles.clearText}>Clear</Text>
                </TouchableOpacity>
              </View>

            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default DateRangeFilterDropdown;

// ── Styles ────────────────────────────────────────────────────────────────────

const baseStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
  },
  panel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    width: 280,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    ...Platform.select({
      web: { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.12,
        shadowRadius: 12,
        elevation: 10,
      },
    }),
  },
  label: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#1A1A1A',
    marginBottom: 6,
  },
  labelTop: {
    marginTop: 14,
  },
  pickerContainer: {
    width: '100%',
  },
  clearRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  clearText: {
    fontSize: 14,
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
});
